import React, { useEffect } from "react";

import { choiceOf, paragraphs, svgSrc, type Here, type Opt, type RoomSection } from "./content";

// deploy 9 — help for a reviewer moving through many cards: which section a card belongs to (an
// intro card with a map before each section), where this card sits ("You are here"), and a few
// options to choose between on a card. Every map is shown as an <img> built from its SVG text,
// never put into the page as markup, so a map can't run script.

// a map image; with onOpen it's a button that shows the map larger
export function MapImg({ svg, alt, onOpen, className }: { svg?: string; alt: string; onOpen?: (svg: string) => void; className?: string }) {
  const src = svgSrc(svg);
  if (!src) return null;
  const img = <img src={src} alt={alt} draggable={false} />;
  if (!onOpen) return <span className={className}>{img}</span>;
  return <button type="button" className={className} aria-label="See the map larger" onClick={(e) => { e.stopPropagation(); onOpen(svg!); }}>{img}</button>;
}

export function MapLightbox({ svg, title, onClose }: { svg: string; title?: string; onClose: () => void }) {
  const src = svgSrc(svg);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!src) return null;
  return (
    <div className="lr-ovl lr-mapbox" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lr-mapbox-in" role="dialog" aria-label={title ? `Map: ${title}` : "Map"}>
        <div className="top"><b>{title || "Map"}</b><button onClick={onClose} aria-label="Close the map">✕</button></div>
        <img src={src} alt={title ? `Map: ${title}` : "Map"} />
      </div>
    </div>
  );
}

// "You are here": a small map with this item highlighted on the left, the path and short facts on the right
export function HereStrip({ here, onOpenMap }: { here?: Here; onOpenMap: (svg: string) => void }) {
  if (!here) return null;
  const lines = (here.lines || []).filter((l) => l && (l.label || l.text));
  const map = svgSrc(here.map) ? here.map : undefined;
  if (!map && !here.path && !lines.length) return null;
  return (
    <div className={`lr-here ${map ? "" : "nomap"}`} onClick={(e) => e.stopPropagation()}>
      {map && <MapImg svg={map} alt="Where this sits" onOpen={onOpenMap} className="lr-here-map" />}
      <div className="lr-here-tx">
        <div className="lr-here-k">You are here</div>
        {here.path && <div className="lr-here-path">{here.path}</div>}
        {!!lines.length && <dl>{lines.map((l, i) => <div key={i}><dt>{l.label}</dt><dd>{l.text}</dd></div>)}</dl>}
      </div>
    </div>
  );
}

// options on a card: a short list per option, the recommended one marked. saved = the choices kept
// with the decision; local = the ones just tapped (not saved until the item is decided)
export function OptionList({ options, saved, local, onPick, hint }: { options?: Opt[]; saved?: (number | null)[]; local?: (number | null)[]; onPick: (option: number, choice: number) => void; hint?: string }) {
  const list = (options || []).filter((o) => o && Array.isArray(o.choices) && o.choices.length);
  if (!list.length) return null;
  return (
    <div className="lr-opts" onClick={(e) => e.stopPropagation()}>
      {(options || []).map((o, k) => {
        if (!o || !Array.isArray(o.choices) || !o.choices.length) return null;
        const on = choiceOf(o, k, saved, local);
        return (
          <div className="lr-opt" key={k}>
            <div className="lbl" id={`opt-${k}-${o.label}`}>{o.label}</div>
            <div role="radiogroup" aria-label={o.label}>
              {o.choices.map((c, j) => (
                <button type="button" role="radio" key={j} aria-checked={on === j} className={on === j ? "on" : ""} onClick={() => onPick(k, j)}>
                  <span className="dot" aria-hidden />
                  <span className="ct"><span className="t">{c.text}</span>{c.note && <span className="n">{c.note}</span>}</span>
                  {o.pick === j && <span className="rec">Recommended</span>}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {hint && <div className="lr-opt-hint">{hint}</div>}
    </div>
  );
}

// the intro card before a section's first item: title, intro, the full map. Nothing to approve;
// notes and highlights work on it (its notes sit on "section:<id>")
export function SectionIntro({ section, onOpenMap, notes }: { section: RoomSection; onOpenMap: (svg: string) => void; notes?: React.ReactNode }) {
  const paras = paragraphs(section.intro);
  return (
    <section className="lr-sintro" data-item={`section:${section.id}`} data-section={section.id}>
      <div className="lr-sintro-k">Section</div>
      <h2>{section.title}</h2>
      {section.sub && <div className="sub">{section.sub}</div>}
      {!!paras.length && <div className="lr-sintro-tx">{paras.map((p, i) => <p key={i}>{p}</p>)}</div>}
      <MapImg svg={section.map} alt={`Map: ${section.title}`} onOpen={onOpenMap} className="lr-sintro-map" />
      {notes}
    </section>
  );
}
